<script lang="ts" setup>
import Button from 'primevue/button';
import Message from 'primevue/message';
import { ref, nextTick, watch, useTemplateRef } from "vue"
import type { MarketType, SymbolFundingRate } from "@/utils/types";
import BellPopover from '../BellPopover.vue';
import AdaptiveDialog from '../AdaptiveDialog.vue';
import TwoLegsModeComponent from './cex-cex/CexCexTwoLegsForm.vue';
import OneLegModeComponent from './cex-cex/CexCexOneLegForm.vue';
import type { Tick } from '@/utils/ticks';

const twoLegsModePanel = useTemplateRef('twoLegsModePanel')
const oneLegModePanel = useTemplateRef('oneLegModePanel')
const twoLegsMode = ref(true)
const visible = ref(false)
const minimized = ref(false)
const emit = defineEmits(['update-user-positions', 'show-positions-modal'])

export interface CexCexOrdersModalProps {
  cacheKey: string
  spread: number | null
  contractSize: number | null
  recommendedAmount?: number | null,
  backSpread: number | null
  priceBuy: number | null
  priceSell: number | null
  twoLegs: boolean
  volBuy: number | null
  volSell: number | null
  backPriceBuy: number | null
  backPriceSell: number | null
  amountStep: number | null
  exBuy: string | null
  marketType: MarketType | null
  latestExSellPrice: number | null
  asset: string | null
  sellAsset: string | null
  rightTicks: Tick[] | null
  exSell: string | null
  fundingRates: Record<string, SymbolFundingRate>
  quoteBuy?: string
  quoteSell?: string
}
export interface InnerCexCexOrdersModalProps extends CexCexOrdersModalProps {
  minimized: boolean
}

const props = defineProps<CexCexOrdersModalProps>()

const toggle = async () => {
  visible.value = !visible.value
}

const onShow = async () => {
  updatePositions()
  await nextTick()
  callAppear()
}


const showPositions = () => {
  emit('show-positions-modal')
}

const updatePositions = () => {
  emit('update-user-positions')
}

defineExpose({
  toggle
})


const callAppear = async () => {
  if (!props.twoLegs) {
    twoLegsMode.value = false
    await oneLegModePanel.value?.onAppears()
    await twoLegsModePanel.value?.onDisappears()
  } else {
    if (twoLegsMode.value) {
      await oneLegModePanel.value?.onDisappears()
      await twoLegsModePanel.value?.onAppears()
    } else {
      await oneLegModePanel.value?.onAppears()
      await twoLegsModePanel.value?.onDisappears()
    }
  }
}


watch(twoLegsMode, async () => {
  if (visible.value) {
    callAppear()
  }
}, { immediate: true })

</script>

<template>
  <AdaptiveDialog @show="onShow" v-model:visible="visible" class="md:w-170 w-full"
    :class="{ 'w-100': minimized, 'lg:w-220 xl:w-250': !minimized }" postion="bottom">
    <template #header>
      <div class="flex items-center">
        <!-- Одноногий и двуногий -->
        <Button v-if="twoLegs" style="height: 40px; width: 40px;" @click="twoLegsMode = !twoLegsMode"
          :label="!twoLegsMode ? '1' : '2'" variant="text" rounded></Button>
        <BellPopover variant="text" :spread-label="$t('openSpread')" :back-spread-label="$t('closeSpread')" rounded>
        </BellPopover>
        <!-- <Button @click="minimized = !minimized" :icon="minimized ? 'pi pi-window-maximize' : 'pi pi-window-minimize'"
          rounded variant="text"></Button> -->
        <span class="ms-2">
          {{ $t('modal.createSwapOrders.openTitle', {
            spread: props.spread ? Number(props.spread).toFixed(2) : '-',
            backSpread: props.backSpread ? Number(props.backSpread).toFixed(2) : '-'
          }) }}
        </span>
      </div>
    </template>
    <Message icon="pi pi-exclamation-circle" class="mt-1 mb-1" severity="error" size="small" v-if="!twoLegs">
      {{ $t("twoLegsNotAvailable") }}
    </Message>
    <TwoLegsModeComponent ref="twoLegsModePanel" v-show="twoLegsMode" v-bind="props" :minimized="minimized"
      :quote-buy="props.quoteBuy" :quote-sell="props.quoteSell" @update-user-positions="updatePositions"
      @show-positions-modal="showPositions">
    </TwoLegsModeComponent>
    <OneLegModeComponent ref="oneLegModePanel" v-show="!twoLegsMode" v-bind="props" :minimized="minimized"
      :quote-buy="props.quoteBuy" :quote-sell="props.quoteSell" @update-user-positions="updatePositions"
      @show-positions-modal="showPositions">
    </OneLegModeComponent>
  </AdaptiveDialog>
</template>
